/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { FieldSet } from '@api';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { checkoutCompleteStripeCcPayment } from 'src/js/enrol/containers/payment/actions/Actions';
import { CheckoutItemRequest } from 'src/js/model/checkout/request/CheckoutItemRequest';
import { updatePaymentSuccessUrl } from '../../common/actions/Actions';
import { IshState } from '../../services/IshState';
import { requestCourse, requestCourseClass, requestProduct } from '../../web/actions/Actions';
import { changePhase, getCartData, sendInitRequest, setPayer } from '../actions/Actions';
import { CheckoutState, Phase } from '../reducers/State';
import CheckoutService from '../services/CheckoutService';
import Concession from './concession/Concession';
import { submitAddContact } from './contact-add/actions/Actions';
import CompanyAddForm from './contact-add/CompanyAddForm';
import ContactAddForm from './contact-add/ContactAddForm';
import ContactEditForm from './contact-edit/ContactEditForm';
import { Messages, Progress } from './Functions';
import { Payment } from './payment/Payment';
import { Result } from './result/Result';
import Summary from './summary/Summary';

export const isOldIE = () => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  return msie !== -1;
};

interface Props {
  phase: Phase;
  page: number;
  onInit: () => void;
  updatePaymentSuccessUrl: (url: string) => void;
  onProceedToPayment: () => void;
  checkoutCompleteStripeCcPayment: () => void;
  getCartData: (payerId) => void;
  setPayer: (payerId) => void;
  changePhase: (phase) => void;
  isNewContact: boolean;
  fetching: boolean;
  childName: any;
  fieldset: FieldSet;
  minAge: number;
  isFirstContact: boolean;
  isEmptyProducts: boolean;
  isOnlyWaitingCoursesInCart: boolean;
  requestCourse: (request: CheckoutItemRequest) => void;
  requestCourseClass: (request: CheckoutItemRequest) => void;
  requestProduct: (request: CheckoutItemRequest) => void;
}

export class Checkout extends React.Component<Props, any> {
  componentDidMount() {
    const { onInit, updatePaymentSuccessUrl, setPayer, getCartData,
      requestCourse, requestCourseClass, checkoutCompleteStripeCcPayment, requestProduct } = this.props;

    onInit();

    const urlParsms = new URLSearchParams(window.location.search);
    const sourcePath = urlParsms.get('sourcePath');
    const courseCodes = urlParsms.get('course');
    const classCodes = urlParsms.get('class');
    const productCodes = urlParsms.get('product');
    const payerId = urlParsms.get('payerId');
    const onCourseSessionId = urlParsms.get('onCourseSessionId');

    if (sourcePath) {
      updatePaymentSuccessUrl(sourcePath);
    }

    if (payerId) {
      setPayer(payerId);
      getCartData(payerId);
    }
    if (courseCodes) {
      courseCodes.split(',').forEach(code => requestCourse({ code, addToCart: true }));
    }
    if (classCodes) {
      classCodes.split(',').forEach(code => requestCourseClass({ code, addToCart: true }));
    }
    if (productCodes) {
      productCodes.split(',').forEach(code => requestProduct({ code, addToCart: true }));
    }
    if (!onCourseSessionId) {
      history.replaceState(null, null, window.location.origin + window.location.pathname);
    } else {
      checkoutCompleteStripeCcPayment();
    }
  }

  render() {
    const {
      phase, page, isNewContact, fetching, childName, fieldset,
      minAge, isFirstContact, isEmptyProducts, isOnlyWaitingCoursesInCart
    } = this.props;

    return (
      <div>
        {isOldIE() && (
        <div
          style={{
            backgroundColor: '#E57373',
            color: 'white',
            padding: '5px',
            borderRadius: '3px',
            lineHeight: '17px',
            marginBottom: '20px',
          }}
        >
          {/* tslint:disable-next-line:max-line-length */}
          Your current browser is not supported. Please ensure you are using an up to date version of Chrome, Firefox or Edge.
        </div>
        )}
        <Progress />
        <Messages />

        {(phase === Phase.AddPayer || phase === Phase.AddContact || phase === Phase.AddContactAsPayer
          || phase === Phase.AddParent || phase === Phase.ChangeParent)
        && (
          <ContactAddForm
            childName={childName}
            phase={phase}
            isFirst={isFirstContact}
            isEmptyProducts={isEmptyProducts}
            isOnlyWaitingCoursesInCart={isOnlyWaitingCoursesInCart}
            onSuccess={submitAddContact}
            onCancel={!isNewContact ? () => this.props.changePhase(page) : undefined}
            fetching={fetching}
            fieldset={fieldset}
            minAge={minAge}
          />
        )}
        {phase === Phase.AddContactAsCompany
        && (
        <CompanyAddForm
          onSuccess={submitAddContact}
          onCancel={() => this.props.changePhase(page)}
          fetching={fetching}
          fieldset={fieldset}
        />
        )}

        {phase === Phase.AddConcession
        && <Concession />}

        {(phase === Phase.EditContact || phase === Phase.ComplementEditContact) && <ContactEditForm />}
        {phase === Phase.Summary && <Summary />}
        {phase === Phase.Payment && <Payment />}
        {phase === Phase.Result && <Result />}
      </div>
    );
  }
}

const getChildFromProps = (state: CheckoutState) => {
  const childId = state.contactAddProcess.forChild;
  if (!childId) return null;

  const child = state.contacts.entities.contact && state.contacts.entities.contact[childId];
  return child ? `${child.firstName} ${child.lastName}` : '';
};

const mapStateToProps = (state: IshState) => ({
  phase: state.checkout.phase,
  page: state.checkout.page,
  isNewContact: !state.checkout.contacts.result.length,
  fetching: state.checkout.fetching,
  childName: getChildFromProps(state.checkout),
  fieldset: CheckoutService.isOnlyWaitingCoursesInCart(state.cart) ? FieldSet.WAITINGLIST : FieldSet.ENROLMENT,
  minAge: state.preferences.minAge,
  isFirstContact: !state.checkout.summary.result.length,
  isEmptyProducts: !state.cart.products.result.length,
  isOnlyWaitingCoursesInCart: CheckoutService.isOnlyWaitingCoursesInCart(state.cart)
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  onInit: (): void => {
    dispatch(sendInitRequest());
  },
  changePhase: (phase) => {
    dispatch(changePhase(phase));
  },
  updatePaymentSuccessUrl: (url) => {
    dispatch(updatePaymentSuccessUrl(url));
  },
  setPayer: id => dispatch(setPayer(id)),
  getCartData: id => dispatch(getCartData(id)),
  requestCourse: (request) => dispatch(requestCourse(request)),
  requestCourseClass: (request) => dispatch(requestCourseClass(request)),
  requestProduct: (request) => dispatch(requestProduct(request)),
  checkoutCompleteStripeCcPayment: () => dispatch(checkoutCompleteStripeCcPayment())
});

export default connect<any, any, any, IshState>(mapStateToProps, mapDispatchToProps)(Checkout);